import { Button, Input, Modal } from '@coinlocally/cly-core';
import { CloseOutline } from '@coinlocally/cly-core/icons';
import SecondCounter from 'shared/hooks';
import { ChangeEvent, FC, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { maskEmail } from 'shared/utils';
import { maskPhone } from 'shared/utils';
import { ConfirmModalContainer } from './confirm-modal.style';
import { POSTUsersOtp, OtpIssueType } from 'api/coinlocally/v1/users';

const ConfirmModal: FC<{
	title: string;
	onClose: () => void;
	note?: string;
	status: boolean;
	activeOtp: { email: string | null; phone: string | null };
	issue: OtpIssueType;
	confirmApi: (otp: string) => Promise<any>;
	successAction:
		| {
				action: 'REDIRECT' | 'CLOSE';
				to?: string;
				redirectionType?: 'INTERNAL' | 'EXTERNAL';
				authType?: 'LOGOUT' | 'NORMAL';
		  }
		| ((token: string) => void)
		| ((data: any) => void);
}> = ({ title, confirmApi, onClose, status, note, activeOtp, successAction, issue }) => {
	const { t } = useTranslation('components', { keyPrefix: 'modals' });
	
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [modalStatus, setModalStatus] = useState<boolean>(status);
	const [emailOtp, setEmailOtp] = useState<string>('');
	const [phoneOtp, setPhoneOtp] = useState<string>('');
	const [emailOtpError, setEmailOtpError] = useState<string | null>(null);
	const [phoneOtpError, setPhoneOtpError] = useState<string | null>(null);

	const [emailActionIsLoading, setEmailActionIsLoading] = useState<boolean>(false);
	const [phoneActionIsLoading, setPhoneActionIsLoading] = useState<boolean>(false);

	const emailRef = useRef<HTMLInputElement>(null);
	const phoneRef = useRef<HTMLInputElement>(null);
	const totpRef = useRef<HTMLInputElement>(null);

	const [multiInputCode, setMultiInputCode] = useState(['', '', '', '', '', '']);

	const multiRefs = useRef([
		useRef<HTMLInputElement>(null),
		useRef<HTMLInputElement>(null),
		useRef<HTMLInputElement>(null),
		useRef<HTMLInputElement>(null),
		useRef<HTMLInputElement>(null),
		useRef<HTMLInputElement>(null),
	]);

	// eslint-disable-next-line
	const [email_days, email_hours, email_minutes, email_seconds, email_setNewCountDown] = SecondCounter(0);
	// eslint-disable-next-line
	const [phone_days, phone_hours, phone_minutes, phone_seconds, phone_setNewCountDown] = SecondCounter(0);

	const navigate = useNavigate();

	useEffect(() => {
		if (activeOtp.email) {
			setEmailActionIsLoading(true);
			POSTUsersOtp({ issue: issue, email: activeOtp.email })
				.then(res => email_setNewCountDown(res.nextResendIn))
				.finally(() => setEmailActionIsLoading(false));
		}
		if (activeOtp.phone) {
			setPhoneActionIsLoading(true);
			POSTUsersOtp({ issue: issue, phoneNumber: activeOtp.phone })
				.then(res => phone_setNewCountDown(res.nextResendIn))
				.finally(() => setPhoneActionIsLoading(false));
		}

		if (isOneMethod()) {
			if (multiRefs.current[0].current) {
				multiRefs.current[0].current.focus();
			}
		} else {
			if (activeOtp.email) {
				if (emailRef.current) {
					emailRef.current.focus();
				}
			} else {
				if (activeOtp.phone) {
					if (phoneRef.current) {
						phoneRef.current.focus();
					}
				} else {
					if (totpRef.current) {
						totpRef.current.focus();
					}
				}
			}
		}
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		const code = multiInputCode.join('');
		if (code.length === 6) {
			onConfirmClickHandler(code);
		}
	}, [multiInputCode]);

	const onSendOtpToEmailClickHandler = () => {
		if (email_minutes + email_seconds === 0) {
			setEmailActionIsLoading(true);
			POSTUsersOtp({ issue: issue, email: activeOtp.email! })
				.then(res => email_setNewCountDown(res.nextResendIn))
				.finally(() => setEmailActionIsLoading(false));
		}
	};

	const onSendOtpToPhoneClickHandler = () => {
		if (phone_minutes + phone_seconds === 0) {
			setPhoneActionIsLoading(true);
			POSTUsersOtp({ issue: issue, phoneNumber: activeOtp.phone! })
				.then(res => phone_setNewCountDown(res.nextResendIn))
				.finally(() => setPhoneActionIsLoading(false));
		}
	};

	const onConfirmClickHandler = (code: string) => {
		if ((!code || code.length !== 6) && ((activeOtp.email && emailOtp.length !== 6) || (activeOtp.phone && phoneOtp.length !== 6))) {
			if (activeOtp.email && emailOtp.length !== 6) {
				setEmailOtpError(t('please-enter-a-valid-otp-token'));
			}
			if (activeOtp.phone && phoneOtp.length !== 6) {
				setPhoneOtpError(t('please-enter-a-valid-otp-token'));
			}
		} else {
			setIsLoading(true);
			confirmApi(
				code,
				// activeOtp.email ? (code ? code : emailOtp) : undefined,
				// activeOtp.phone ? (code ? code : phoneOtp) : undefined,
				// activeOtp.totp ? (code ? code : googleOtp) : undefined,
			)
				.then(res => {
					if (typeof successAction === 'function') {
						onCloseClickHandler();
						successAction(Object.keys(res).length === 1 ? res.securityChangeToken : { ...res });
					} else {
						if (successAction.authType === 'LOGOUT') {
							localStorage.removeItem('access_auth_token');
							localStorage.removeItem('refresh_auth_token');

							if (successAction.to) {
								window.location.href = successAction.to;
							}
						}

						if (successAction.action === 'REDIRECT') {
							if (successAction.redirectionType === 'INTERNAL') {
								navigate(successAction.to!, { replace: true });
							} else {
								window.location.href = successAction.to!;
							}
						} else {
							setIsLoading(false);
							onCloseClickHandler();
						}
					}
				})
				.catch(error => {
					setIsLoading(false);
					if (isOneMethod()) {
						setTimeout(() => {
							multiRefs.current[5].current?.focus();
						}, 300);
					}
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	};

	const onCloseClickHandler = () => {
		if (isLoading || emailActionIsLoading || phoneActionIsLoading) {
		} else {
			setModalStatus(false);
			setTimeout(() => {
				onClose();
			}, 300);
		}
	};

	const OnKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (isOneMethod()) {
		} else {
			if (e.key === 'Enter') {
				onConfirmClickHandler(multiInputCode.join(''));
			}
		}
		if (e.key === 'Escape' && !isLoading) {
			onClose();
		}
	};

	const isOneMethod = () => {
		let count = 0;
		if (activeOtp.email !== null) {
			count++;
		}
		if (activeOtp.phone !== null) {
			count++;
		}

		return count === 1;
	};

	const multiInputChangeHandler = (e: ChangeEvent<HTMLInputElement>, index: 1 | 2 | 3 | 4 | 5 | 6) => {
		if (e.target.value.length === 6 && !isNaN(+e.target.value)) {
			setMultiInputCode(e.target.value.split(''));
			multiRefs.current[index - 1].current?.blur();
		} else {
			let codes = [...multiInputCode];
			codes[index - 1] = e.target.value.length > 1 ? e.target.value.charAt(0) : e.target.value;
			if (e.target.value.length === 2 && index !== 6) {
				codes[index] = e.target.value.charAt(1);
				if (index < 5) {
					multiRefs.current[index + 1].current?.focus();
				}
			}
			if (index !== 6 && e.target.value.length === 1) {
				multiRefs.current[index].current?.focus();
			}
			if (index !== 1 && e.target.value.length === 0) {
				multiRefs.current[index - 2].current?.focus();
			}
			if (index === 6 && e.target.value.length === 1) {
				multiRefs.current[5].current?.blur();
			}
			setMultiInputCode(codes);
		}
	};

	const MultiInputOnKeyUp = (e: KeyboardEvent<HTMLInputElement>, refIndex: number) => {
		if (['e', 'E', '+', '-'].includes(e.key)) {
			e.preventDefault();
		}

		if (e.key === 'Backspace') {
			if (multiRefs.current[refIndex].current?.value === '' && refIndex > 0) {
				multiRefs.current[refIndex - 1].current?.focus();
			}
		}
	};

	return (
		<Modal maxWidth={472} onClose={onCloseClickHandler} status={modalStatus}>
			<ConfirmModalContainer onKeyDown={OnKeyDown}>
				<div className='confirm_header'>
					<div className='title'>{title}</div>
					<CloseOutline onClick={onCloseClickHandler} />
				</div>

				<div className='confirm_body' style={isOneMethod() ? { marginBottom: 0 } : {}}>
					{note && (
						<div className='note_container'>
							<span>{t('note')}</span> {note}
						</div>
					)}

					{isOneMethod() ? (
						<div className='separated_container'>
							{(activeOtp.email || activeOtp.phone) && (
								<div className='label'>
									{t('enter-the-6-digit-code-will-be-sent-to')}{' '}
									<strong>{activeOtp.email ? maskEmail(activeOtp.email) : maskPhone(activeOtp.phone!)}</strong>
								</div>
							)}

							{/* {activeOtp.totp && <div className='label'>Please enter your Google 2FA code</div>} */}

							<div className={`inputs_container ${isLoading && 'disabled'}`}>
								<Input
									variant='outline'
									disabled={isLoading}
									type='number'
									className='input_single_number'
									onKeyUp={e => MultiInputOnKeyUp(e, 0)}
									ref={multiRefs.current[0]}
									value={multiInputCode[0]}
									onChange={e => multiInputChangeHandler(e, 1)}
								/>
								<Input
									disabled={isLoading}
									variant='outline'
									type='number'
									className='input_single_number'
									onKeyUp={e => MultiInputOnKeyUp(e, 1)}
									ref={multiRefs.current[1]}
									value={multiInputCode[1]}
									onChange={e => multiInputChangeHandler(e, 2)}
								/>
								<Input
									disabled={isLoading}
									variant='outline'
									type='number'
									className='input_single_number'
									onKeyUp={e => MultiInputOnKeyUp(e, 2)}
									ref={multiRefs.current[2]}
									value={multiInputCode[2]}
									onChange={e => multiInputChangeHandler(e, 3)}
								/>
								<Input
									disabled={isLoading}
									variant='outline'
									type='number'
									className='input_single_number'
									onKeyUp={e => MultiInputOnKeyUp(e, 3)}
									ref={multiRefs.current[3]}
									value={multiInputCode[3]}
									onChange={e => multiInputChangeHandler(e, 4)}
								/>
								<Input
									disabled={isLoading}
									variant='outline'
									type='number'
									className='input_single_number'
									onKeyUp={e => MultiInputOnKeyUp(e, 4)}
									ref={multiRefs.current[4]}
									value={multiInputCode[4]}
									onChange={e => multiInputChangeHandler(e, 5)}
								/>
								<Input
									disabled={isLoading}
									variant='outline'
									type='number'
									className='input_single_number'
									onKeyUp={e => MultiInputOnKeyUp(e, 5)}
									ref={multiRefs.current[5]}
									value={multiInputCode[5]}
									onChange={e => multiInputChangeHandler(e, 6)}
								/>
							</div>

							<div className='code_container'>
								<div
									className='get_code'
									onClick={() => {
										activeOtp.email ? onSendOtpToEmailClickHandler() : onSendOtpToPhoneClickHandler();
									}}
								>
									{activeOtp.email
										? email_minutes + email_seconds === 0
											? t('getcode')
											: `0${email_minutes}:${email_seconds < 10 ? '0' + email_seconds : email_seconds}`
										: phone_minutes + phone_seconds === 0
										? t('getcode')
										: `0${phone_minutes}:${phone_seconds < 10 ? '0' + phone_seconds : phone_seconds}`}
								</div>
							</div>
						</div>
					) : (
						<>
							{activeOtp.email && (
								<Input
									variant='filled'
									type='number'
									id='email_otp'
									value={emailOtp}
									ref={emailRef}
									onChange={e => {
										if (emailOtpError) setEmailOtpError(null);
										setEmailOtp(e.target.value);
									}}
									className='email_otp'
									placeholder={t('email-verification-code') || ''}
									action={
										email_minutes + email_seconds === 0
											? 'GetCode'
											: `0${email_minutes}:${email_seconds < 10 ? '0' + email_seconds : email_seconds}`
									}
									isActionLoading={emailActionIsLoading}
									error={emailOtpError}
									onActionClickHandler={onSendOtpToEmailClickHandler}
									label={`${t('enter-the-6-digit-code-will-be-sent-to')} ${maskEmail(activeOtp.email)}`}
								/>
							)}
							{activeOtp.phone && (
								<Input
									variant='filled'
									type='number'
									id='phone_otp'
									value={phoneOtp}
									ref={phoneRef}
									onChange={e => {
										if (phoneOtpError) setPhoneOtpError(null);
										setPhoneOtp(e.target.value);
									}}
									className='phone_otp'
									placeholder={t('phone-verification-code') || ''}
									action={
										phone_minutes + phone_seconds === 0
											? t('getcode')
											: `0${phone_minutes}:${phone_seconds < 10 ? '0' + phone_seconds : phone_seconds}`
									}
									onActionClickHandler={onSendOtpToPhoneClickHandler}
									isActionLoading={phoneActionIsLoading}
									error={phoneOtpError}
									label={`${t('enter-the-6-digit-code-will-be-sent-to')} ${maskPhone(activeOtp.phone)}`}
								/>
							)}
						</>
					)}
				</div>
				{isOneMethod() ? (
					<></>
				) : (
					<div className='confirm_action'>
						<Button
							variant='outline'
							size='medium'
							onClick={onCloseClickHandler}
							disabled={isLoading || emailActionIsLoading || phoneActionIsLoading}
						>
							{t('cancel')}
						</Button>
						<Button
							variant='primary'
							size='medium'
							isLoading={isLoading}
							onClick={() => {
								onConfirmClickHandler(multiInputCode.join());
							}}
						>
							{t('confirm')}
						</Button>
					</div>
				)}
			</ConfirmModalContainer>
		</Modal>
	);
};

export default ConfirmModal;
